import type { RequestOptions } from '@@/plugin-request/request';
import type { RequestConfig } from '@umijs/max';
import { message, notification } from 'antd';
import { getAccessToken, clearSessionToken } from './utils/auth';
import { history } from '@umijs/max';
import { PageEnum } from './enums/pagesEnums';

declare const API_PREFIX: string | undefined;

enum ErrorShowType {
  SILENT = 0,
  WARN_MESSAGE = 1,
  ERROR_MESSAGE = 2,
  NOTIFICATION = 3,
  REDIRECT = 9,
}

interface ResponseStructure {
  success?: boolean;
  data: any;
  errorCode?: number;
  errorMessage?: string;
  showType?: ErrorShowType;
  code?: number;
  msg?: string;
  message?: string;
}

const DEFAULT_API_PREFIX = '/dev-api';

const createAuthClear = () => {
  let locked = false;
  return (msg: string) => {
    if (locked) return;
    locked = true;
    clearSessionToken();
    message.error(msg);
    history.push(PageEnum.LOGIN);
    setTimeout(() => {
      locked = false;
    }, 300);
  };
};

const clearAuth = createAuthClear();

const showError = (type: ErrorShowType | undefined, errorMessage?: string, errorCode?: number) => {
  switch (type) {
    case ErrorShowType.SILENT:
      break;
    case ErrorShowType.WARN_MESSAGE:
      message.warning(errorMessage);
      break;
    case ErrorShowType.ERROR_MESSAGE:
      message.error(errorMessage);
      break;
    case ErrorShowType.NOTIFICATION:
      notification.open({ description: errorMessage, message: errorCode });
      break;
    case ErrorShowType.REDIRECT:
      clearAuth(errorMessage || '授权已过期');
      break;
    default:
      message.error(errorMessage);
  }
};

/**
 * @name 全局请求配置
 * 包含：错误处理、请求拦截器、响应拦截器
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  timeout: 25000,

  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {},
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      if (error.data && error.data.code !== undefined && error.data.code !== 200) {
        throw error;
      }

      if (error.response) {
        message.error(`Response status: ${error.response.status}`);
      } else if (error.request) {
        message.error('无响应，请重试');
      } else {
        message.error('请求异常，请重试');
      }
    },
  },

  requestInterceptors: [
    (config: RequestOptions) => {
      const apiPrefix = API_PREFIX ?? DEFAULT_API_PREFIX;
      if (apiPrefix && typeof config.url === 'string') {
        config.url = config.url.replace(/^\/api(?=\/)/, apiPrefix);
      }

      const token = getAccessToken();
      if (token) {
        config.headers = config.headers ?? {};
        config.headers.Authorization = `Bearer ${token}`;
      }

      return config;
    },
  ],

  responseInterceptors: [
    (response: any) => {
      const { data } = response;
      if (data && typeof data === 'object' && data.code !== undefined && data.code !== 200) {
        const errorCode = data.errorCode ?? data.code;
        const errorMessage =
          data.errorMessage || data.msg || data.message || '请求失败，请稍后重试';

        if (data.showType !== undefined) {
          showError(data.showType, errorMessage, errorCode);
          return;
        }

        if (errorCode !== undefined && errorCode !== 200) {
          if (errorCode === 401) {
            clearAuth('登录已过期');
            return;
          }

          message.error(errorMessage);
          throw ({ data:data });
        }
      }
      return response;
    },
  ],
};
